import { z } from 'zod';
import { Parser, Ty } from '@cotera/era';
import type { ApiSchemaClass, ApiSchemaDefinition } from './types';
import {
  emptyObjectResponseSchema,
  warehouseConnectionResponseSchema,
} from './common-responses';

const streamSpecifierSchema = z.discriminatedUnion('t', [
  z.object({
    t: z.literal('manifest'),
    id: z.string(),
    entityName: z.string(),
  }),
  z.object({
    t: z.literal('graph'),
    relIdentity: z.tuple([z.number(), z.number()]),
    colIdentity: z.tuple([z.number(), z.number()]),
  }),
]);

export const warehouseRunMaterializationBodyV20260601Schema = z.object({
  viewName: z.string(),
  schema: z.string(),
});

export type WarehouseRunMaterializationBody = z.infer<
  typeof warehouseRunMaterializationBodyV20260601Schema
>;

export const warehouseSnapshotEventStreamBodyV20260601Schema = z.object({
  streamSpecifier: streamSpecifierSchema,
});

export type WarehouseSnapshotEventStreamBody = z.infer<
  typeof warehouseSnapshotEventStreamBodyV20260601Schema
>;

export const warehouseConnectionCreateBodyV20260601Schema = z.object({
  displayName: z.string(),
  credentials: z.custom<Parser.Credentials.WarehouseCredentials>(),
  skipSetup: z.boolean(),
});

export type WarehouseConnectionCreateBody = z.infer<
  typeof warehouseConnectionCreateBodyV20260601Schema
>;

export const warehouseConnectionCreateTableBodyV20260601Schema = z.object({
  tableName: z.string(),
  headerRow: z.array(z.string()),
  dataRows: z.array(z.array(z.custom<Ty.Scalar>())),
  columnTypes: z.record(z.string(), z.custom<Ty.ExtendedAttributeType>()),
});

export type WarehouseConnectionCreateTableBody = z.infer<
  typeof warehouseConnectionCreateTableBodyV20260601Schema
>;

export class WarehouseRunMaterializationV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'warehouse-run-materialization-2026-06-01' as const;

  readonly name = WarehouseRunMaterializationV20260601Schema.version;
  readonly schema = warehouseRunMaterializationBodyV20260601Schema;
  readonly response = emptyObjectResponseSchema;
}

export class WarehouseSnapshotEventStreamV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version =
    'warehouse-snapshot-event-stream-2026-06-01' as const;

  readonly name = WarehouseSnapshotEventStreamV20260601Schema.version;
  readonly schema = warehouseSnapshotEventStreamBodyV20260601Schema;
  readonly response = emptyObjectResponseSchema;
}

export class WarehouseConnectionCreateV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'warehouse-connection-create-2026-06-01' as const;

  readonly name = WarehouseConnectionCreateV20260601Schema.version;
  readonly schema = warehouseConnectionCreateBodyV20260601Schema;
  readonly response = warehouseConnectionResponseSchema;
}

export class WarehouseConnectionCreateTableV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version =
    'warehouse-connection-create-table-2026-06-01' as const;

  readonly name = WarehouseConnectionCreateTableV20260601Schema.version;
  readonly schema = warehouseConnectionCreateTableBodyV20260601Schema;
  readonly response = emptyObjectResponseSchema;
}

export const WAREHOUSE_API_SCHEMAS = [
  WarehouseRunMaterializationV20260601Schema,
  WarehouseSnapshotEventStreamV20260601Schema,
  WarehouseConnectionCreateV20260601Schema,
  WarehouseConnectionCreateTableV20260601Schema,
] as const satisfies readonly ApiSchemaClass[];
